import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/auth/services';
import { RestService } from 'src/app/services/rest.service';
import { StatisticsService } from 'src/app/services/statistics.service';
import { ToolsService } from 'src/app/services/tools.service';
import { TranslationService } from 'src/app/services/translation.service';
import { INFORMER_ROUTE_REGISTER_STORAGE_KEY } from 'src/app/interfaces';
import { RegisterUserFormValues } from '../interfaces';
import { TripType } from '../interfaces/register-user.response';
import { LocalizedName } from '../../custom-route/shared/save-recommeded-route-tech-mode-view/interfaces';
import { localizedEntryValue } from '../../custom-route/shared/save-recommeded-route-tech-mode-view/helpers/resolve-localized-name';

/** Lo que hay que recordar del formulario hasta que la ruta tenga token. */
interface InformerRouteRegisterData {
  travelerType: number | null;
  categories: number[];
  subCategories: number[];
  days: number;
}

/**
 * HRS-2990 (fase 5): estadística de las rutas predefinidas del informador.
 *
 * En este modo el formulario no pasa por `post-register` —la ruta la crea el
 * asistente—, así que el tipo de viajero y los intereses que el técnico rellena
 * no quedaban en ninguna parte. Dani lo pidió explícito: deben guardarse siempre
 * a nivel estadístico, pero **sin filtrar la ruta** (la ruta se arma con los
 * contenidos que se añaden desde la home, no con estos campos). El payload que
 * va al asistente nunca los ha llevado, así que eso se cumple solo.
 *
 * El guardado ocurre después de navegar al planner, así que los valores se
 * persisten con el mismo TTL que el resto de claves del modo y se consumen al
 * guardar la ruta, que es cuando ya hay token al que asociarlos. Al ser de un
 * solo uso, editar más tarde una ruta ya guardada no genera otro registro.
 */
@Injectable({ providedIn: 'root' })
export class InformerRouteRegisterService {
  private _http = inject(HttpClient);
  private _tools = inject(ToolsService);
  private _restService = inject(RestService);
  private _authService = inject(AuthService);
  private _statisticsService = inject(StatisticsService);
  private _translationService = inject(TranslationService);

  /** Guarda los datos estadísticos del formulario de una ruta nueva. */
  public remember(values: RegisterUserFormValues): void {
    const categories = (values.categories ?? []);

    const data: InformerRouteRegisterData = {
      travelerType: (values.tripType as unknown as TripType)?.id ?? null,
      categories: categories.map(category => Number(category.id)).filter(Number.isInteger),
      subCategories: categories
        .flatMap(category => category.subcategories ?? [])
        .map(subcategory => Number(subcategory))
        .filter(Number.isInteger),
      days: daysBetween(values.startDate, values.endDate),
    };

    this._tools.setWithExpiry(INFORMER_ROUTE_REGISTER_STORAGE_KEY, data);
  }

  public clear(): void {
    localStorage.removeItem(INFORMER_ROUTE_REGISTER_STORAGE_KEY);
  }

  /**
   * Manda el registro estadístico de la ruta recién guardada, si el formulario
   * dejó datos pendientes. Un fallo aquí no debe alterar el flujo: la ruta ya
   * está guardada y el técnico ya ha visto el aviso de éxito.
   */
  public sendPendingRegister(routeToken: string, routeName?: LocalizedName): void {
    const data = this._take();
    if (!data || !routeToken) return;

    const device = this._restService.device();

    const body = {
      clientID: device?.client ?? null,
      distributorID: device?.distributorID ?? null,
      deviceID: device?.device ?? 0,
      channelID: device?.channel?.toString() ?? null,
      appID: device?.appID ?? null,
      type: device?.type ?? null,
      owner: device?.type === 'distributor' ? 'distributor' : 'client',
      language: this._translationService.displayLanguage(),
      routeToken,
      // 0 a propósito: el listado de rutas del CMS lee esta misma tabla filtrando
      // por `recommended`, así que con 1 esta fila —que es estadística, sin ruta—
      // aparecía allí como una ruta vacía. La ruta de verdad vive en el asistente y
      // queda referenciada por `routeToken`.
      recommended: 0,
      informerMode: 'predefined-route',
      days: data.days,
      travelerType: data.travelerType,
      categories: data.categories,
      subCategories: data.subCategories,
      users_webapp_id: this._authService.user()?.id ?? null,
      ...this._statisticsService.cmsMetricsIdentity(),
      name_route: plainNameMap(routeName),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(body));

    this._http.post(`${environment.apiUrlBase}/api/pwa-save-route`, formData)
      .pipe(catchError(error => {
        console.error('Error saving informer route register:', error);
        return of(null);
      }))
      .subscribe();
  }

  /** Lee y descarta: el registro es de un solo uso por ruta creada. */
  private _take(): InformerRouteRegisterData | null {
    const stored = this._tools.getWithExpiry(INFORMER_ROUTE_REGISTER_STORAGE_KEY);
    this.clear();

    if (!stored) return null;

    return {
      travelerType: Number.isInteger(stored.travelerType) ? stored.travelerType : null,
      categories: Array.isArray(stored.categories) ? stored.categories.filter(Number.isInteger) : [],
      subCategories: Array.isArray(stored.subCategories) ? stored.subCategories.filter(Number.isInteger) : [],
      days: Number.isInteger(stored.days) && stored.days > 0 ? stored.days : 1,
    };
  }
}

/** `DD/MM/YYYY` -> días de estancia, contando el primero. */
function daysBetween(startDate: string | null, endDate: string | null): number {
  const start = parseFormDate(startDate);
  const end = parseFormDate(endDate) ?? start;
  if (!start || !end) return 1;

  const diff = Math.round((end.getTime() - start.getTime()) / 86400000);
  return diff > 0 ? diff + 1 : 1;
}

function parseFormDate(value: string | null): Date | null {
  if (!value) return null;

  const [day, month, year] = value.split('/').map(Number);
  if (!day || !month || !year) return null;

  return new Date(year, month - 1, day);
}

/**
 * `{ lang: { value, custom } }` -> `{ lang: value }`, que es el formato con el
 * que `pwa-save-route` guarda el nombre.
 */
function plainNameMap(routeName?: LocalizedName): Record<string, string> | null {
  if (!routeName) return null;

  const plain: Record<string, string> = {};
  Object.entries(routeName).forEach(([lang, entry]) => {
    const value = localizedEntryValue(entry);
    if (value) plain[lang] = value;
  });

  return Object.keys(plain).length > 0 ? plain : null;
}
